/**
 * Technician Toolbox Hooks
 * React Query hooks for private tool inventory CRUD
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type {
  TechnicianTool,
  TechnicianToolMedia,
  TechnicianToolWithMedia,
  CreateToolInput,
  UpdateToolInput,
  ToolboxCategory,
  ParsedToolData,
} from '@/types/toolbox';
import { toast } from 'sonner';

// ============================================
// Utility Functions
// ============================================

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============================================
// Identity Resolution
// ============================================

/**
 * Resolve the current user's technician_profile_id
 * Returns null if user is not a technician
 */
export function useCurrentTechnicianProfileId() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['current-technician-profile-id', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Primary path: active technician_users link (shop-attached users)
      const { data: linkData, error: linkError } = await supabase
        .from('technician_users')
        .select(`
          technician_id,
          technicians!inner(technician_profile_id)
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      if (linkError) throw linkError;

      if (linkData) {
        const technician = linkData.technicians as unknown as { technician_profile_id: string };
        if (technician?.technician_profile_id) return technician.technician_profile_id;
      }

      // Fallback path: passport-mode users (decoupled, no technician_users row)
      const { data: profileData, error: profileError } = await supabase
        .from('technician_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      return profileData?.id || null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

/**
 * Check if current user owns a specific technician profile
 */
export function useIsOwnProfile(technicianProfileId: string | undefined) {
  const { data: currentProfileId, isLoading } = useCurrentTechnicianProfileId();

  return {
    isOwnProfile: !!currentProfileId && currentProfileId === technicianProfileId,
    isLoading,
  };
}

// ============================================
// Tools CRUD
// ============================================

/**
 * Fetch all tools for a technician profile
 */
export function useToolboxTools(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['toolbox-tools', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];

      const { data: tools, error } = await supabase
        .from('technician_tools')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('name', { ascending: true });

      if (error) throw error;

      // Fetch media for all tools
      const toolIds = tools.map(t => t.id);
      const { data: media, error: mediaError } = await supabase
        .from('technician_tool_media')
        .select('*')
        .in('tool_id', toolIds);

      if (mediaError) throw mediaError;

      // V-E-2 R151-HIGH-1: batch signed-URL generation. Previously this was
      // N round-trips (one createSignedUrl per tool inside a Promise.all map).
      // For a tech with 200 tools that meant 200 sequential storage calls.
      // The plural API issues all signed URLs in a single round trip.
      const primaryMediaByTool = new Map<string, TechnicianToolMedia>();
      const primaryPaths: string[] = [];
      for (const tool of tools) {
        const toolMedia = (media || []).filter(m => m.tool_id === tool.id) as TechnicianToolMedia[];
        const primary = toolMedia.find(m => m.is_primary) || toolMedia[0];
        if (primary) {
          primaryMediaByTool.set(tool.id, primary);
          primaryPaths.push(primary.storage_path);
        }
      }

      const signedUrlByPath = new Map<string, string>();
      if (primaryPaths.length > 0) {
        const { data: signedList } = await supabase.storage
          .from('toolbox-media')
          .createSignedUrls(primaryPaths, 3600);
        for (const entry of signedList || []) {
          if (entry.signedUrl && entry.path) {
            signedUrlByPath.set(entry.path, entry.signedUrl);
          }
        }
      }

      const toolsWithMedia: TechnicianToolWithMedia[] = tools.map((tool) => {
        const toolMedia = (media || []).filter(m => m.tool_id === tool.id) as TechnicianToolMedia[];
        const primary = primaryMediaByTool.get(tool.id);
        const primaryPhotoUrl = primary ? signedUrlByPath.get(primary.storage_path) || null : null;
        return {
          ...tool,
          category: tool.category as ToolboxCategory,
          media: toolMedia,
          primary_photo_url: primaryPhotoUrl,
        };
      });

      return toolsWithMedia;
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * Fetch a single tool with media
 */
export function useTool(toolId: string | undefined) {
  return useQuery({
    queryKey: ['toolbox-tool', toolId],
    queryFn: async () => {
      if (!toolId) return null;

      const { data: tool, error } = await supabase
        .from('technician_tools')
        .select('*')
        .eq('id', toolId)
        .single();

      if (error) throw error;

      const { data: media } = await supabase
        .from('technician_tool_media')
        .select('*')
        .eq('tool_id', toolId);

      const toolMedia = (media || []) as TechnicianToolMedia[];
      const primaryMedia = toolMedia.find(m => m.is_primary) || toolMedia[0];

      let primaryPhotoUrl: string | null = null;
      if (primaryMedia) {
        const { data: signedData } = await supabase.storage
          .from('toolbox-media')
          .createSignedUrl(primaryMedia.storage_path, 3600);
        primaryPhotoUrl = signedData?.signedUrl || null;
      }

      return {
        ...tool,
        category: tool.category as ToolboxCategory,
        media: toolMedia,
        primary_photo_url: primaryPhotoUrl,
      } as TechnicianToolWithMedia;
    },
    enabled: !!toolId,
  });
}

/**
 * Create a new tool
 */
export function useCreateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateToolInput) => {
      const { data, error } = await supabase
        .from('technician_tools')
        .insert({
          technician_profile_id: input.technician_profile_id,
          name: input.name,
          category: input.category,
          brand: input.brand || null,
          model: input.model || null,
          part_number: input.part_number || null,
          serial_number: input.serial_number || null,
          purchase_price: input.purchase_price || null,
          purchase_date: input.purchase_date || null,
          notes: input.notes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', data.technician_profile_id] });
      toast.success('Tool added to your toolbox');
    },
    onError: (error: Error) => {
      console.error('Failed to create tool:', error);
      toast.error('Failed to add tool');
    },
  });
}

/**
 * Update an existing tool
 */
export function useUpdateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateToolInput) => {
      const { id, ...updates } = input;
      const { data, error } = await supabase
        .from('technician_tools')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', data.technician_profile_id] });
      queryClient.invalidateQueries({ queryKey: ['toolbox-tool', data.id] });
      toast.success('Tool updated');
    },
    onError: (error: Error) => {
      console.error('Failed to update tool:', error);
      toast.error('Failed to update tool');
    },
  });
}

/**
 * Delete a tool (and all its media)
 */
export function useDeleteTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ toolId, technicianProfileId }: { toolId: string; technicianProfileId: string }) => {
      // First, get all media to delete from storage
      const { data: media } = await supabase
        .from('technician_tool_media')
        .select('storage_path')
        .eq('tool_id', toolId);

      // Delete from storage
      if (media && media.length > 0) {
        const paths = media.map(m => m.storage_path);
        await supabase.storage.from('toolbox-media').remove(paths);
      }

      // Delete the tool (cascades to media table)
      const { error } = await supabase
        .from('technician_tools')
        .delete()
        .eq('id', toolId);

      if (error) throw error;
      return { toolId, technicianProfileId };
    },
    onSuccess: ({ technicianProfileId, toolId }) => {
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', technicianProfileId] });
      queryClient.removeQueries({ queryKey: ['toolbox-tool', toolId] });
      toast.success('Tool removed from your toolbox');
    },
    onError: (error: Error) => {
      console.error('Failed to delete tool:', error);
      toast.error('Failed to remove tool');
    },
  });
}

// ============================================
// Tool Media
// ============================================

/**
 * Upload a photo for a tool
 */
export function useUploadToolPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      toolId,
      technicianProfileId,
      file,
      isPrimary = false,
    }: {
      toolId: string;
      technicianProfileId: string;
      file: File;
      isPrimary?: boolean;
    }) => {
      // Generate unique path
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const storagePath = `${technicianProfileId}/${toolId}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('toolbox-media')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // If setting as primary, unset other primaries first
      if (isPrimary) {
        await supabase
          .from('technician_tool_media')
          .update({ is_primary: false })
          .eq('tool_id', toolId);
      }

      // Insert media record
      const { data, error } = await supabase
        .from('technician_tool_media')
        .insert({
          tool_id: toolId,
          technician_profile_id: technicianProfileId,
          storage_path: storagePath,
          is_primary: isPrimary,
        })
        .select()
        .single();

      if (error) {
        // Clean up storage if DB insert fails
        await supabase.storage.from('toolbox-media').remove([storagePath]);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', data.technician_profile_id] });
      queryClient.invalidateQueries({ queryKey: ['toolbox-tool', data.tool_id] });
      toast.success('Photo uploaded');
    },
    onError: (error: Error) => {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    },
  });
}

/**
 * Delete a tool photo
 */
export function useDeleteToolPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, storagePath, technicianProfileId, toolId }: {
      mediaId: string;
      storagePath: string;
      technicianProfileId: string;
      toolId: string;
    }) => {
      // Delete from storage
      await supabase.storage.from('toolbox-media').remove([storagePath]);

      // Delete media record
      const { error } = await supabase
        .from('technician_tool_media')
        .delete()
        .eq('id', mediaId);

      if (error) throw error;
      return { technicianProfileId, toolId };
    },
    onSuccess: ({ technicianProfileId, toolId }) => {
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', technicianProfileId] });
      queryClient.invalidateQueries({ queryKey: ['toolbox-tool', toolId] });
      toast.success('Photo deleted');
    },
    onError: (error: Error) => {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    },
  });
}

// ============================================
// AI Photo Analysis
// ============================================

/**
 * Parse a tool photo using AI vision to extract tool information
 */
export function useParseToolPhoto() {
  return useMutation({
    mutationFn: async (file: File): Promise<ParsedToolData> => {
      const base64 = await fileToBase64(file);
      
      const { data, error } = await supabase.functions.invoke('parse-tool-photo', {
        body: { 
          image: base64, 
          mimeType: file.type 
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to analyze photo');
      }

      // Handle rate limit or payment errors in response
      if (data?.error) {
        throw new Error(data.error);
      }

      return data as ParsedToolData;
    },
    onError: (error: Error) => {
      console.error('Failed to parse tool photo:', error);
      // Don't show toast here - let the component handle it for better UX
    },
  });
}
